import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, retry, Subject, timer } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { Utils } from './utils.service';
import { IStandConversationImage, IStandConversationMessage, IStandConversationDone } from './planograms.service';
import { ITimeTrackingDelete, ITimeTrackingRecord } from './time-tracking.service';
import { IAssortmentType, ISyncProductMessage } from '../../product/models/models';
import { Dashboard } from '../../home/models/dashboard';
import { ILimitation } from '../models/limitation';
import { SessionService } from './session.service';
import { IUserSave, UsersActivityData } from '../models/usersActivityData';
import { INotification, INotificationsCount } from '../models/notifications';
import { IChatMessage, IChatReadMessage } from '../models/chat';
import { IChat } from '../components/all-planogram-chats/chat';

interface ISubscribeElement {
	groupName: SocketGroup;
	params: Object;
}

export enum SocketGroup {
    Chat = 'chat',
	ChatList = 'chat_list',
	DemoAssortmentType = 'demo_assortment_type',
	DemoHomePage = 'demo_home_page',
	EquipmentUsersActivity = 'equipment_user_activity',
	LatestUpdates = 'latest_updates',
	Limitations = 'limitations',
	Notification = 'notification',
	Jobs = 'jobs',
	PlanUsersActivity = 'plan_user_activity',
	PlanogramUsersActivity = 'planogram_user_activity',
	StandConversation = 'stand_conversation',
	StandProductsUpdate = 'stand_products_update',
	SyncProducts = 'sync_products',
	TimeTracker = 'time_tracker',
	UpdateChat = 'update_chat',
	UpdateExecution = 'update_execution'
}

enum SocketType {
	AssortmentType = 'update_demo_assortment_type',
	ChatUnreadCount = 'chat_unread_count',
	ChatListUnreadCount = 'chat_list_unread_count',
	ChatListUpdate = 'chat_list_update',
    ChatNewMessage = 'chat_new_message',
    ChatEditMessage = 'edit_message',
    ChatDeleteMessage = 'delete_message',
    ChatReadMessage = 'read_message',
    ChatNewPinnedMessage = 'chat_new_pinned_message',
    ChatDeletePinnedMessage = 'chat_delete_pinned_message',
    ChatRestoreMessage = 'restore_message',
    ChatReactToMessage = 'react_to_message',
    ChatDeleteReaction = 'delete_reaction',
    ChatTyping = 'chat_typing',
	ConversationNewMessage = 'new_message',
	ConversationReadMessage = 'done_message',
	ConversationUpdateImage = 'update_image',
    EquipmentSave = 'equipment_save',
    EquipmentInfoSave = 'equipment_info_save',
	JobUpdate = 'update_item',
	JobDelete = 'delete_item',
    EquipmentUsersActivity = 'equipment_user_activity',
	LatestUpdates = 'latest_updates',
	LimitationsChanged = 'limitations_changed',
	Notification = 'push_notification',
	NotificationsCount = 'notifications_count',
	NotificationsRead = 'notifications_read',
	NotificationsDeleted = 'notifications_deleted',
	NotificationsArchived = 'notifications_archived',
	NotificationsUnArchived = 'notifications_unarchived',
    PlanSave = 'plan_save',
    PlanUsersActivity = 'plan_user_activity',
    PlanogramSave = 'planogram_save',
    PlanogramUsersActivity = 'planogram_user_activity',
	ProductSyncStart = 'sync_start',
	ProductSyncFinish = 'sync_finish',
	StandProductsUpdate = 'stand_products_update',
	TimeTracker = 'time_tracker',
	TimeTrackerDelete = 'time_tracker_delete',
	TimeTrackerDeleteALl = 'time_tracker_delete_all',
	UpdateChat = 'update_chat',
	UpdateExecution = 'update_execution',
	UpdateHomePage = 'update_home_page'
}

type SocketMessage = {
	group: SocketGroup;
	type: SocketType;
	data: any;
}

const SOCKET_URL: string = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws/updates/`,
	START_RECONNECT_STEP: number = 10000,
	MAX_TRY_ATTEMPTS: number = 100;

@Injectable({
	providedIn: 'root'
})
export class SocketService {

	assortmentType: Subject<IAssortmentType> = new Subject<IAssortmentType>();
	chatUnreadCount: BehaviorSubject<{ unread: number }> = new BehaviorSubject<{ unread: number }>({ unread: 0 });
	chatListUnreadCount: BehaviorSubject<{ unread: number }> = new BehaviorSubject<{ unread: number }>({ unread: 0 });
	chatListUpdate: Subject<IChat> = new Subject<IChat>();
    chatNewMessage: Subject<{ message: IChatMessage }> = new Subject<{ message: IChatMessage }>();
    chatEditMessage: Subject<{ message: IChatMessage }> = new Subject<{ message: IChatMessage }>();
    chatDeleteMessage: Subject<{ message_ids: Array<number> }> = new Subject<{ message_ids: Array<number> }>();
    chatReadMessage: BehaviorSubject<IChatReadMessage> = new BehaviorSubject<IChatReadMessage>(null);
    chatNewPinnedMessage: Subject<{ message: IChatMessage }> = new Subject<{ message: IChatMessage }>();
    chatDeletePinnedMessage: Subject<{ message_id: number }> = new Subject<{ message_id: number }>();
    chatRestoreMessage: Subject<{ message: IChatMessage }> = new Subject<{ message: IChatMessage }>();
    chatReactToMessage: Subject<{ emoji: string; user_id: number; message_id: number }> = new Subject<{ emoji: string; user_id: number; message_id: number }>();
    chatDeleteReaction: Subject<{ emoji: string; user_id: number; message_id: number }> = new Subject<{ emoji: string; user_id: number; message_id: number }>();
    chatTyping: Subject<{ id: number; full_name: string }> = new Subject<{ id: number; full_name: string }>();
	conversationNewMessage: Subject<IStandConversationMessage> = new Subject<IStandConversationMessage>();
	conversationReadMessage: Subject<IStandConversationDone> = new Subject<IStandConversationDone>();
	conversationUpdateImage: Subject<IStandConversationImage> = new Subject<IStandConversationImage>();
    equipmentSave: Subject<IUserSave> = new Subject<IUserSave>();
    equipmentInfoSave: Subject<IUserSave> = new Subject<IUserSave>();
	jobsUpdate: Subject<any> = new Subject<any>();
	jobsDelete: Subject<{ job_id: string }> = new Subject<{ job_id: string }>();
    equipmentUsersActivity: BehaviorSubject<UsersActivityData> = new BehaviorSubject<UsersActivityData>(null);
	latestUpdates: Subject<void> = new Subject<void>();
	limitationsChanged: Subject<{ limitations: Array<ILimitation> }> = new Subject<{ limitations: Array<ILimitation> }>();
	notification: Subject<INotification> = new Subject<INotification>();
	notificationsCount: BehaviorSubject<INotificationsCount> = new BehaviorSubject<INotificationsCount>({ total: 0, archived: 0, unread: 0 });
	notificationsDeleted: Subject<{ ids: Array<number> }> = new Subject<{ ids: Array<number> }>();
	notificationsRead: Subject<{ ids: Array<number> }> = new Subject<{ ids: Array<number> }>();
	notificationsArchived: Subject<{ ids: Array<number> }> = new Subject<{ ids: Array<number> }>();
	notificationsUnArchived: Subject<{ ids: Array<number> }> = new Subject<{ ids: Array<number> }>();
    planSave: Subject<IUserSave> = new Subject<IUserSave>();
    planUsersActivity: BehaviorSubject<UsersActivityData> = new BehaviorSubject<UsersActivityData>(null);
    planogramSave: Subject<IUserSave> = new Subject<IUserSave>();
    planogramUsersActivity: BehaviorSubject<UsersActivityData> = new BehaviorSubject<UsersActivityData>(null);
	productSyncStart: Subject<ISyncProductMessage> = new Subject<ISyncProductMessage>();
	productSyncFinish: Subject<ISyncProductMessage> = new Subject<ISyncProductMessage>();
	standProductsUpdate: Subject<void> = new Subject<void>();
	timeTracker: Subject<ITimeTrackingRecord> = new Subject<ITimeTrackingRecord>();
	timeTrackerDelete: Subject<ITimeTrackingDelete> = new Subject<ITimeTrackingDelete>();
	timeTrackingDeleteAll: Subject<void> = new Subject<void>();
	updateChat: Subject<void> = new Subject<void>();
	updateExecution: Subject<void> = new Subject<void>();
	updateHomePage: Subject<Dashboard> = new Subject<Dashboard>();

	private _socket: WebSocketSubject<any>;
	private _connected: boolean = false;
	private _subscribedGroups: Array<ISubscribeElement> = [];

	private readonly typeSubjects: Map<SocketType, Subject<any> | BehaviorSubject<any>> = new Map<SocketType, Subject<any> | BehaviorSubject<any>>([
		[SocketType.AssortmentType, this.assortmentType],
		[SocketType.ChatUnreadCount, this.chatUnreadCount],
		[SocketType.ChatListUnreadCount, this.chatListUnreadCount],
		[SocketType.ChatListUpdate, this.chatListUpdate],
        [SocketType.ChatNewMessage, this.chatNewMessage],
        [SocketType.ChatEditMessage, this.chatEditMessage],
        [SocketType.ChatDeleteMessage, this.chatDeleteMessage],
        [SocketType.ChatReadMessage, this.chatReadMessage],
        [SocketType.ChatNewPinnedMessage, this.chatNewPinnedMessage],
        [SocketType.ChatDeletePinnedMessage, this.chatDeletePinnedMessage],
        [SocketType.ChatRestoreMessage, this.chatRestoreMessage],
        [SocketType.ChatReactToMessage, this.chatReactToMessage],
        [SocketType.ChatDeleteReaction, this.chatDeleteReaction],
        [SocketType.ChatTyping, this.chatTyping],
		[SocketType.ConversationNewMessage, this.conversationNewMessage],
		[SocketType.ConversationReadMessage, this.conversationReadMessage],
		[SocketType.ConversationUpdateImage, this.conversationUpdateImage],
        [SocketType.EquipmentSave, this.equipmentSave],
        [SocketType.EquipmentInfoSave, this.equipmentInfoSave],
		[SocketType.JobUpdate, this.jobsUpdate],
		[SocketType.JobDelete, this.jobsDelete],
        [SocketType.EquipmentUsersActivity, this.equipmentUsersActivity],
		[SocketType.LatestUpdates, this.latestUpdates],
		[SocketType.LimitationsChanged, this.limitationsChanged],
		[SocketType.Notification, this.notification],
		[SocketType.NotificationsCount, this.notificationsCount],
		[SocketType.NotificationsDeleted, this.notificationsDeleted],
		[SocketType.NotificationsRead, this.notificationsRead],
		[SocketType.NotificationsArchived, this.notificationsArchived],
		[SocketType.NotificationsUnArchived, this.notificationsUnArchived],
        [SocketType.PlanSave, this.planSave],
        [SocketType.PlanUsersActivity, this.planUsersActivity],
        [SocketType.PlanogramSave, this.planogramSave],
        [SocketType.PlanogramUsersActivity, this.planogramUsersActivity],
		[SocketType.ProductSyncStart, this.productSyncStart],
		[SocketType.ProductSyncFinish, this.productSyncFinish],
		[SocketType.StandProductsUpdate, this.standProductsUpdate],
		[SocketType.TimeTracker, this.timeTracker],
		[SocketType.TimeTrackerDelete, this.timeTrackerDelete],
		[SocketType.TimeTrackerDeleteALl, this.timeTrackingDeleteAll],
		[SocketType.UpdateChat, this.updateChat],
		[SocketType.UpdateExecution, this.updateExecution],
		[SocketType.UpdateHomePage, this.updateHomePage]
	]);

	constructor(private router: Router, private sessionService: SessionService) {
		if (!this.router.url.includes('landing') && !this.sessionService.isFramePage) this._init();
	}

	private _connect(): void {
		if (this._socket) {
			this._socket.complete();
			this._socket.unsubscribe();
		}
		this._socket = webSocket({
			url: SOCKET_URL,
			protocol: [
				this.sessionService.token,
				String(this.sessionService.clientId)
			],
			openObserver: {
				next: () => this._onConnected()
			},
			closeObserver: {
				next: () => {
					if (this._connected) this._init();
				}
			}
		});
	}

	private _init(): void {
		this._connected = false;
		this._connect();
		this._socket.asObservable()
			.pipe(
				retry({
					count: MAX_TRY_ATTEMPTS,
					delay: (_, retryCount: number) => timer(1000 + (retryCount - 1) * START_RECONNECT_STEP)
				})
			)
			.subscribe((data: SocketMessage) => {
				if (this.typeSubjects.has(data.type)) this.typeSubjects.get(data.type).next(data.data);
			});
	}

	private _onConnected(): void {
		this._connected = true;
		this._subscribedGroups.forEach((element: ISubscribeElement) => {
			this._subscribe(element.groupName, element.params);
		});
	}

	private _subscribe(groupName: SocketGroup, params?: Object): void {
		if (!this._connected) return;
		const data = {
			data: { name: groupName },
			type: 'subscribe'
		};
		if (params) Utils.assignObject(data.data, { params: params });
		this._socket.next(data);
	}

	private _unsubscribe(groupName: SocketGroup, params?: any): void {
		if (!this._connected) return;
		const data = {
			data: { name: groupName },
			type: 'unsubscribe'
		};
		if (params) Utils.assignObject(data.data, { params: params });
		this._socket.next(data);
	}

	addSubscription(groupName: SocketGroup, params?: Object): void {
		const alreadySubscription = this._subscribedGroups.find((element: ISubscribeElement) =>
			element.groupName === groupName && (element.params === params || Utils.equalObjects(element.params, params))
		);
		if (!alreadySubscription) {
			this._subscribedGroups.push({
				groupName: groupName,
				params: params
			});
			this._subscribe(groupName, params);
		}
	}

	deleteSubscription(groupName: SocketGroup, params?: Object): void {
		const alreadySubscriptionIndex = this._subscribedGroups.findIndex((element: ISubscribeElement) =>
			element.groupName === groupName && (element.params === params || Utils.equalObjects(element.params, params))
		);
		if (alreadySubscriptionIndex !== -1) {
			this._subscribedGroups.splice(alreadySubscriptionIndex, 1);
			this._unsubscribe(groupName, params);
		}
	}
}
